import React, { useState } from 'react';
import Plot from 'react-plotly.js';

const ProfilingInfo = ({ profilingInfo }) => {
  const [showPlotModal, setShowPlotModal] = useState(false);
  const [selectedPlotData, setSelectedPlotData] = useState(null);
  const [selectedColumnName, setSelectedColumnName] = useState('');

  if (!profilingInfo || !Array.isArray(profilingInfo) || profilingInfo.length === 0) {
    return <div className="text-gray-600">No profiling information available.</div>;
  }

  // Safe JSON parsing function
  const safeJsonParse = (jsonString) => {
    if (!jsonString) return {};
    
    try {
      // Replace NaN with null before parsing
      const cleanedJson = jsonString.replace(/:\s*NaN/g, ': null');
      return JSON.parse(cleanedJson);
    } catch (error) {
      console.error('Error parsing JSON:', error);
      return {};
    }
  };

  // Calculate summary statistics
  const columnsAnalyzed = profilingInfo.length;
  const totalRows = profilingInfo.length > 0 ? (() => {
    try {
      return profilingInfo[0]?.count || 0;
    } catch {
      return 0;
    }
  })() : 0;

  // Helper function to calculate null percentage
  const calculateNullPercentage = (profile) => {
    try {
      return profile?.nullPercentage || 0;
    } catch {
      return 0;
    }
  };

  // Helper function to extract key statistics
  const extractStatistics = (profile) => {
    try {
      const stats = [];
      
      if (profile?.mean !== null && profile?.mean !== undefined && !isNaN(profile.mean)) {
        stats.push(`Mean: ${profile.mean}`);
      }
      if (profile?.max !== null && profile?.max !== undefined) {
        stats.push(`Max: ${profile.max}`);
      }
      if (profile?.min !== null && profile?.min !== undefined) {
        stats.push(`Min: ${profile.min}`);
      }
      if (profile?.stddev !== null && profile?.stddev !== undefined && !isNaN(profile.stddev)) {
        stats.push(`Std Dev: ${profile.stddev}`);
      }
      
      return stats;
    } catch {
      return [];
    }
  };

  const handleViewPlot = (profile) => {
    try {
      setSelectedPlotData(profile);
      setSelectedColumnName(profile.column_name || 'Unknown Column');
      setShowPlotModal(true);
    } catch (error) {
      console.error('Error handling plot data:', error);
    }
  };

  const renderPlotModal = () => {
    if (!showPlotModal || !selectedPlotData) return null;

    const generatePlotData = () => {
      const data = selectedPlotData;
      
      // Use the chart data from the API response
      if (data?.chart?.data && Array.isArray(data.chart.data)) {
        return data.chart.data;
      }
      
      // Fallback to default empty plot
      return [{
        x: [],
        y: [],
        type: 'scatter'
      }];
    };

    const layout = {
      ...selectedPlotData?.chart?.layout,
      title: selectedPlotData?.chart?.layout?.title || `Profiling Results for ${selectedColumnName}`,
      height: 400,
      margin: { l: 50, r: 50, t: 50, b: 50 }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Profiling Visualization
              </h3>
              <button
                onClick={() => setShowPlotModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                ✕
              </button>
            </div>
            
            <div className="w-full">
              {/* Plot - Full Width */}
              <div className="bg-gray-50 rounded-lg p-4 w-full">
                <Plot
                  data={generatePlotData()}
                  layout={layout}
                  config={{ displayModeBar: false }}
                  style={{ width: '100%', height: '400px' }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h4 className="text-sm font-medium text-gray-500 mb-1">Columns Analyzed</h4>
          <p className="text-2xl font-bold text-gray-900">{columnsAnalyzed}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h4 className="text-sm font-medium text-gray-500 mb-1">Rows Profiled</h4>
          <p className="text-2xl font-bold text-gray-900">{totalRows.toLocaleString()}</p>
        </div>
      </div>

      {/* Profiling Results Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto" style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <table className="min-w-full">
            <thead className="bg-[#0000a0] sticky top-0 z-10">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Column (Data Type)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">CDE</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Business Term</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Null %</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider w-48">Statistics</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {profilingInfo.map((profile, idx) => {
                const nullPercentage = calculateNullPercentage(profile);
                const statistics = extractStatistics(profile);
                
                return (
                  <tr key={idx} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {profile?.column_name || 'N/A'} ({profile?.type || 'N/A'})
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {profile?.isCde ? 'Yes' : 'No'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 max-w-xs truncate" title={profile?.businessterm}>
                      {profile?.businessterm || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {nullPercentage}%
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 w-48">
                      {statistics.map((stat, statIdx) => (
                        <div key={statIdx} className="mb-1">
                          <span className="font-semibold">{stat.split(':')[0]}:</span> {stat.split(':')[1]}
                        </div>
                      ))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      <button
                        onClick={() => handleViewPlot(profile)}
                        className="text-blue-600 hover:text-blue-800 font-medium cursor-pointer"
                      >
                        VIEW PLOT
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Plot Modal */}
      {renderPlotModal()}
    </div>
  );
};

export default ProfilingInfo; 